import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box } from "@mui/material";
import {
  styled,
  Typography,
  Grid
} from "@mui/material";
import { useFormik } from "formik";
import { EditableColumnDef, EditableTable } from "../../../../shared/components/table/inline-edit-table";
import { DatePicker } from "@mui/x-date-pickers";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { MRT_ColumnDef } from "material-react-table";
import * as Yup from "yup";
import { StyledButton } from "../../../../shared/components/buttons/custom-button";
import { useFormActions } from "../../../../shared/hooks/form-builder.hooks";
import useRecordsStore, { Pagination } from "../../../../shared/hooks/useRecordStore";
import { LoaderComponent } from "../../../../shared/components/loaders/page-loader";
import { useTransactionCertificateStore } from "../../../../store/transaction-certificates.store";
import { TCAppConfig } from "../../../../shared/app-configs/tc-app-configs";
import { useApi } from "../../../../shared/hooks/useApi";
import { CustomSelect, CustomTextField } from "../../../../shared/components/inputfields/form-elements";
import { dtrackItService } from "../../../transaction-certificates/tc-service/dtrackit.service";
import FormBuilder from "../../../../shared/components/form-builder/form-builder";

export const PageWrapper = styled("div")({
  padding: "0 25px 0px 25px",
  display: "grid",
  overflowY: "auto",
  flexDirection: "column",
  gap: "12px",
  flex: 1,
});

export interface RawMaterialDetails {
    rawMaterialCode: string; // Text field
    rawMaterialPercentage: number; // Numeric field
  }

export interface SourceDetails {
    organizationName: string; // Text field
    teId: string;
    address: string;
    state: string;
    country: string;
  }  

  const validationSchema = Yup.object().shape({
    rawMaterialCode: Yup.string().required("TC Standard is required"),
    rawMaterialPercentage: Yup.number()
      .typeError("Percentage must be a number")
      .positive("Percentage must be positive")
      .required("Percentage is required"),
  });

const RawMaterialDeclaration = () => {
  const formRef = useRef<any>(null);
  const navigate = useNavigate();
  const { resetAllDatas } = useFormActions("")
  const { setDelayReason, setTCIssueDate } = useTransactionCertificateStore();
  const [ data, setData ] = useState<any>([]);
  const [isResetEnabled, setResetEnable] = useState(false);
  const [editingRowIndex, setEditingRowIndex] = useState<number | null>(null);
    const [rawMaterialCodes, setRawMaterialCodes] = useState([]);

  const { executeGet } = useApi();

  const [pagination, setPagination] = useState<Pagination>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState<number>(0);

  useEffect(() => {
      const getRawMaterialCodes = async () => {
        try {
          const result: any = await dtrackItService.getRawMaterialCodes()
  
          console.log(`result getProductDetails ${JSON.stringify(result)}`)
  
          const rawMaterials = result.data.map((t: any) => ({ value: t.code_1, label: `${t.description_1} (${t.code_1})` }))
          console.log(`rawMaterials ${JSON.stringify(rawMaterials)}`)
          setRawMaterialCodes(rawMaterials);
        } catch (err) {
          console.log(`Error in Fetching Organic Farm Standard Code ${err}`)
        }
      };
      
      getRawMaterialCodes()
    }, [])

  const formik = useFormik<RawMaterialDetails>({
      initialValues: {
        rawMaterialCode: "",
        rawMaterialPercentage: 0,
      },
      validationSchema,
      validateOnChange: true,
      validateOnBlur: true,
      onSubmit: () => {
        // Submission logic if needed
      },
    });

      const handleCellChange = (
        rowIndex: number,
        columnId: string,
        value: any
      ) => {
        formik.setFieldValue(columnId, value);
        setData((prevData: any[]) =>
          prevData.map((row, index) =>
            index === rowIndex ? { ...row, [columnId]: value } : row
          )
        );
      };
    
      const handleAddNewRow = () => {
        const newRow: RawMaterialDetails = {
          rawMaterialCode: "",
          rawMaterialPercentage: 0,
        };
        setData((prevData: any) => [...prevData, newRow]);
        setEditingRowIndex(data.length);
        formik.setValues(newRow);
        formik.setTouched({});
      };
    
      const handleCancelEdit = () => {
        if (editingRowIndex !== null) {
          const isLastRow = editingRowIndex === data.length;
          const currentRowValues = formik.values;
          const isEmptyRow = Object.values(currentRowValues).every(
            (value) => value === "" || (Array.isArray(value) && value.length === 0)
          );
    
          if (isLastRow && isEmptyRow) {
            setData((prevData: string | any[]) => prevData.slice(0, -1));
          } else {
            formik.setValues(data[editingRowIndex]);
          }
        }
        setEditingRowIndex(null);
        formik.resetForm();
      };
    
      const handleSaveRow = () => {
        if (editingRowIndex === null || !formik.isValid) return;
    
        const updatedRow = formik.values;
        setData((prevData: any[]) =>
          prevData.map((row, index) =>
            index === editingRowIndex ? updatedRow : row
          )
        );
        setEditingRowIndex(null);
        formik.resetForm();
      };

 const columns: EditableColumnDef<RawMaterialDetails>[] = [
     {
       accessorKey: "rawMaterialCode",
       header: "Product Raw Material Code",
       renderEditCell: ({ value, onChange }) => (
         <CustomSelect
             label=""
             name="rawMaterialCode"
             value={value}
             options={rawMaterialCodes}
             onChange={(e) => {
               onChange(e.target.value)
             }}
           />
       ),
     },
     {
       accessorKey: "rawMaterialPercentage",
       header: "ProductRaw Material Percentage",
       renderEditCell: ({ value, onChange }) => (
         <CustomTextField
           name="rawMaterialPercentage"
           value={value}
           onChange={(e) => onChange(e.target.value)}
           placeholder="Enter value"
           sx={{mb:2}}
         />
        
       ),
     },
   ];


   const sourceColumns: EditableColumnDef<SourceDetails>[] = [
    {
      accessorKey: "organizationName",
      header: "Source Organization Name",
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="organizationName"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter Organization Name"
        />
      ),
    },
    {
      accessorKey: "teId",
      header: "TE-ID",
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="teId"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter TE-ID"
        />
      ),
    },
    {
      accessorKey: "address",
      header: "Address",
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="address"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter Address"
        />
      ),
    },
    {
      accessorKey: "state",
      header: "State",
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="state"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter State"
        />
      ),
    },
    {
      accessorKey: "country",
      header: "Country",
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="country"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter Country"
        />
      ),
    },
  ]; 

  useEffect(() => {
    const loadData = async () => {

    };
    loadData();
  }, [pagination.pageIndex, pagination.pageSize]);

  useEffect(() => {
    setDelayReason(false)
    setTCIssueDate("")
    resetAllDatas();
  }, []);

  const handleEditRow = (rowIndex: number) => {
    setEditingRowIndex(rowIndex);
    formik.setValues(data[rowIndex]);
  };

  const handleDeleteRow = (rowIndex: number) => {
    const updatedData = data.filter((_: any, index: number) => index !== rowIndex);
    setData(updatedData);
  };

   const formConfig = {
      pageName: "raw-material-declaration-page",
      initialValues: {
        certifiedOrganisationName: "", // For multi-select
        coTEId: "",
        rawMaterialCode: ""
      },
      validationSchema: Yup.object().shape({
        certifiedOrganisationName: Yup.string().nullable(),
        coTEId: Yup.string().nullable(),
        rawMaterialCode: Yup.string().nullable(),
      }),
      sections: [
        {
          fields: [
            {
                label: "Certified Organisation Name",
                name: "certifiedOrganisationName",
                type: "search",
                gridSize: { xs: 12, md: 3, lg: 3 },
                required: true,
                value: "",
                props: {
                  placeholder: "Search using Organisation Name",
                  variant: "outlined",
                  displayKey: "organisation_name",
                  onChange: () => {}, // Empty handler for now
                  onSearch: () => {}, // Empty handler for now
                  fullWidth: true,
              },
            },
            {
                label: "CO TE-ID",
                name: "coTEId",
                type: "search",
                gridSize: { xs: 12, md: 3, lg: 3 },
                required: true,
                value: "",
                props: {
                  placeholder: "Search using TE-ID",
                  variant: "outlined",
                  displayKey: "te_id",
                  onChange: () => {}, // Empty handler for now
                  onSearch: () => {}, // Empty handler for now
                  fullWidth: true,
              },
            }, {
              label: "Shipment Number",
              name: "shipmentNumber",
              type: "text",
              gridSize: { xs: 12, md: 3, lg: 3 },
              required: true,
              props: {
                placeholder: "Enter Shipment Number"
              },
            },
            {
              label: "Shipment Date",
              name: "shipmentDate",
              type: "date",
                gridSize: { xs: 12, md: 3, lg: 3 },
                required: true,
                props: {
                  placeholder: "Select Shipment Date",
              },
            },
            {
                label: "Invoice Date",
                name: "invoiceDate",
                type: "date",
                gridSize: { xs: 12, md: 3, lg: 3 },
                required: true,
                props: {
                  placeholder: "Select Invoice Date"
              },
            },
            {
              label: "Order Number",
              name: "orderNumber",
              type: "text",
              gridSize: { xs: 12, md: 3, lg: 3 },
              required: true,
              props: {
                placeholder: "Enter Order Number"
              },
            },
            {
                label: "Product Category",
                name: "productCategory",
                type: "select",
                gridSize: { xs: 12, md: 3, lg: 3 },
                required: true,
                value: "",
                props: {
                  placeholder: "Select Product Category",
                  options: [], // Empty options for now
                  onChange: () => {},
              },
            },
            {
                label: "Product Detail",
                name: "productDetail",
                type: "select",
                gridSize: { xs: 12, md: 3, lg: 3 },
                required: true,
                value: "",
                props: {
                  placeholder: "Select Product Detail",
                  options: [], // Empty options for now
                  onChange: () => {}, // Empty handler for now
              },
            },
            {
              label: "Weight (kg)",
              name: "weight",
              type: "text",
              gridSize: { xs: 12, md: 3, lg: 3 },
              required: true,
              props: {
                placeholder: "Enter the Weight"
              },
            }
          ],
        },
      ],
      handleSubmit: async (values: any) => {},
    };

  return (
      <Box
            component="main"
            sx={{ flexGrow: 1, p: 1, border: "2px solid rgba(128, 128, 128, .2)", my: 4 }}
        >
          
                <FormBuilder
                    formConfig={formConfig}
                    onSubmit={formConfig.handleSubmit}
                    formRef={formRef}
                    />
              {/* <Box display="flex" mb={3} justifyContent="flex-end" width="100%">
                <StyledButton  disabled={!formik.isValid} onClick={handleAddNewRow} sx={{ mb: 2 }}>
                  Add Raw Material
                </StyledButton>
              </Box>
              <EditableTable
                data={data}
                columns={columns}
                onSaveRow={handleSaveRow}
                onCellChange={handleCellChange}
                editingRowIndex={editingRowIndex}
                onEditRow={handleEditRow}
                onCancelEdit={handleCancelEdit}
                onDeleteRow={handleDeleteRow}
              /> */}
            </Box>

  );
};

export default RawMaterialDeclaration;
